/**
 * Pending-gift-count realtime helpers — `useGetGiftsPendingCount` declares
 * these against `morfi.user.{userId}.gift.v0`.
 *
 * The subject dual-publishes the same `GiftEvent` to BOTH the sender and
 * the recipient. The indexer sets `direction = SENT | RECEIVED` per
 * subject so this hook (recipient-side only) can apply the +1 / -1
 * delta correctly without inspecting subject strings.
 *
 * `applyPendingCountDelta`:
 *   - direction = RECEIVED && kind = CREATED → +1 (new pending received)
 *   - direction = RECEIVED && kind = CLAIMED → -1 (clamped to 0)
 *   - direction = SENT (any kind) → no-op (sent-side doesn't count)
 *
 * `applyPendingValueDelta`:
 *   - same conditions; mutates the D6 string `pendingValue` by
 *     ±`amountD6`. Uses bigint string math to preserve D6 precision.
 *
 * Both helpers no-op when `direction` is UNSPECIFIED — the public
 * `morfi.gift.{g}.event.v0` subject ships with UNSPECIFIED so it
 * wouldn't double-count if the same helper accidentally subscribed.
 */

/** Numeric `morfi.v1.GiftEventKind`. */
const KIND_CREATED = 1;
const KIND_CLAIMED = 2;

/** Numeric `morfi.v1.GiftDirection`. */
const DIRECTION_RECEIVED = 2;

function coerceKind(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    switch (trimmed) {
      case 'CREATED':
      case 'GIFT_EVENT_KIND_CREATED':
        return KIND_CREATED;
      case 'CLAIMED':
      case 'GIFT_EVENT_KIND_CLAIMED':
        return KIND_CLAIMED;
      default:
        return 0;
    }
  }
  return 0;
}

function coerceDirection(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    switch (trimmed) {
      case 'SENT':
      case 'GIFT_DIRECTION_SENT':
        return 1;
      case 'RECEIVED':
      case 'GIFT_DIRECTION_RECEIVED':
        return DIRECTION_RECEIVED;
      default:
        return 0;
    }
  }
  return 0;
}

/** Direction-aware ±1 delta to `pendingCount`. */
export function applyPendingCountDelta<T extends { readonly pendingCount?: number }>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  if (coerceDirection(raw.direction) !== DIRECTION_RECEIVED) return prev;
  const kind = coerceKind(raw.kind);
  const current = typeof prev.pendingCount === 'number' ? prev.pendingCount : 0;
  if (kind === KIND_CREATED) {
    return { ...prev, pendingCount: current + 1 };
  }
  if (kind === KIND_CLAIMED) {
    const next = current - 1;
    return { ...prev, pendingCount: next < 0 ? 0 : next };
  }
  return prev;
}

/** Direction-aware ±`amountD6` delta to `pendingValue` (D6 string). */
export function applyPendingValueDelta<T extends { readonly pendingValue?: string }>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  if (coerceDirection(raw.direction) !== DIRECTION_RECEIVED) return prev;
  const kind = coerceKind(raw.kind);
  const amount = parseAmountD6(raw.amountD6);
  if (amount === 0n) return prev;
  const current = parseAmountD6(prev.pendingValue ?? '0');
  if (kind === KIND_CREATED) {
    return { ...prev, pendingValue: (current + amount).toString() };
  }
  if (kind === KIND_CLAIMED) {
    const next = current - amount;
    return { ...prev, pendingValue: (next < 0n ? 0n : next).toString() };
  }
  return prev;
}

function parseAmountD6(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return BigInt(Math.trunc(v));
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!/^\d+$/.test(trimmed)) return 0n;
    try {
      return BigInt(trimmed);
    } catch {
      return 0n;
    }
  }
  return 0n;
}
